import { DATE_FORMAT_PREFERENCE_KEY, DEFAULT_DATE_FORMAT } from '@/constant/date-formats'
import { useOrganisationTimeZone } from '@/hooks/use-organisation-timezone'
import { useUserPreference } from '@/hooks/use-user-preference'
import { formatInTimeZone } from '@/utils/timezone.utils'
import moment from 'moment'

/**
 * Hook for formatting dates based on user preferences
 *
 * @example
 * const { formatDate, formatDateTime } = useDateFormat()
 * formatDate('2024-01-01') // Returns formatted date based on user preference
 */
export const useDateFormat = () => {
  const timeZone = useOrganisationTimeZone()
  const { userPreferences } = useUserPreference()

  /**
   * Get the user's preferred date format
   * Falls back to default if not set
   */
  const getDateFormat = (): string => {
    const preference = userPreferences?.find((p) => p.key === DATE_FORMAT_PREFERENCE_KEY)
    return preference?.value || DEFAULT_DATE_FORMAT
  }

  /**
   * Whether a value has no meaningful time component (stored as midnight UTC).
   * These are date-only values (e.g. "2026-07-30T00:00:00.000Z") picked from a
   * calendar, so converting them to another timezone could shift the day.
   */
  const isDateOnly = (date: string | Date): boolean => {
    const utc = moment.utc(date)
    return utc.hour() === 0 && utc.minute() === 0 && utc.second() === 0 && utc.millisecond() === 0
  }

  /**
   * Remove time tokens (hours, minutes, seconds, AM/PM) and their leftover
   * separators from a moment format string, keeping only the date portion.
   * Escaped literals (e.g. `[at]`) are dropped first so their letters aren't
   * partially stripped, and localized date-time tokens map to their date-only form.
   * Case-sensitive so month `MM` is kept while minute `mm` is removed.
   */
  const stripTimeTokens = (format: string): string =>
    format
      .replace(/\[[^\]]*\]/g, '')
      .replace(/LTS?/g, '')
      .replace(/LLLL/g, 'dddd, LL')
      .replace(/llll/g, 'ddd, ll')
      .replace(/LLL/g, 'LL')
      .replace(/lll/g, 'll')
      .replace(/[hHkmsSAa]/g, '')
      .replace(/[:.]/g, '')
      .replace(/\s+/g, ' ')
      .trim()

  /**
   * Format a date using the user's preferred format
   *
   * @param date - Date string, Date object, or moment object
   * @param customFormat - Optional custom format to override user preference
   * @returns Formatted date string
   */
  const formatDate = (date: string | Date | null | undefined, customFormat?: string): string => {
    if (!date) return '-'
    if (!moment(date).isValid()) return '-'

    const format = customFormat || getDateFormat()

    // Date-only values have no real time, so drop the time tokens and render in
    // UTC so the calendar date stays correct regardless of the organisation's timezone.
    if (isDateOnly(date)) return moment.utc(date).format(stripTimeTokens(format))

    return formatInTimeZone(new Date(date), timeZone, format)
  }

  /** Format a date with the user's preferred format, without the time. */
  const formatDateOnly = (date: string | Date | null | undefined): string =>
    formatDate(date, stripTimeTokens(getDateFormat()))

  return {
    formatDate,
    formatDateOnly,
    dateFormat: getDateFormat(),
  }
}
